import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MapPinIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { ROUTES } from '../../../../utils/routes';

interface CoproOk {
  immatriculation: string;
  name: string;
  addressMatched: boolean;
  rnicName: string;
  rnicAddress: string;
  rnicCity: string | null;
  rnicPostalCode: string | null;
}

interface CoproWeHave {
  immatriculation: string;
  name: string;
  existingCoproId: number;
  existingCoproName: string;
}

interface CoproNotInRnic {
  immatriculation: string;
  name: string;
  rowData?: Record<string, unknown>;
  reason?: string;
}

interface AddressMatchFailed {
  immatriculation: string;
  name: string;
  reason: string;
}

interface RnicNotInFile {
  immatriculation: string;
  name: string;
  rnicAddress: string;
}

interface Summary {
  totalInFile: number;
  totalOk: number;
  totalWeHave: number;
  totalNotInRnic: number;
  totalAddressFailed: number;
  totalRnicNotInFile: number;
}

export interface ComparisonResult {
  coprosOk: CoproOk[];
  coprosWeHave: CoproWeHave[];
  coprosNotInRnic: CoproNotInRnic[];
  addressMatchFailed: AddressMatchFailed[];
  rnicNotInFile: RnicNotInFile[];
  summary: Summary;
}

interface ComparisonResultDisplayProps {
  comparisonResult: ComparisonResult;
}

type TabType = 'ok' | 'weHave' | 'notInRnic' | 'addressFailed' | 'rnicNotInFile';

function StatCard({
  icon,
  title,
  value,
  isActive,
  onClick,
  colorClass = 'text-gray-500',
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  isActive: boolean;
  onClick: () => void;
  colorClass?: string;
}) {
  return (
    <div
      className={`flex cursor-pointer items-center space-x-4 rounded-lg bg-gray-50 p-4 shadow hover:bg-gray-100 ${
        isActive ? 'ring-2 ring-rgpd-blue' : ''
      }`}
      onClick={onClick}
    >
      <div className={colorClass}>{icon}</div>
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-semibold">{value}</p>
      </div>
    </div>
  );
}

const ComparisonResultDisplay: React.FC<ComparisonResultDisplayProps> = ({ comparisonResult }) => {
  const { coprosOk, coprosWeHave, coprosNotInRnic, addressMatchFailed, rnicNotInFile, summary } = comparisonResult;
  const navigate = useNavigate();


  // Default to first non-empty tab
  const getDefaultTab = (): TabType => {
    if (coprosOk.length > 0) {return 'ok';}
    if (coprosWeHave.length > 0) {return 'weHave';}
    if (coprosNotInRnic.length > 0) {return 'notInRnic';}
    if (addressMatchFailed.length > 0) {return 'addressFailed';}
    if (rnicNotInFile.length > 0) {return 'rnicNotInFile';}
    return 'ok';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getDefaultTab());

  type TableRow = CoproOk | CoproWeHave | CoproNotInRnic | AddressMatchFailed | RnicNotInFile;
  const exportToExcel = (data: TableRow[], filename: string, headers: { key: string; label: string }[]) => {
    const worksheetData = data.map((item) => {
      const row: Record<string, string | number | boolean | null | undefined> = {};
      headers.forEach(({ key, label }) => {
        row[label] = item[key] ?? '';
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Données');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const renderTable = (
    data: TableRow[],
    columns: { key: string; label: string; render?: (value: unknown, row: TableRow) => React.ReactNode }[],
    exportFilename: string,
  ) => {
    if (data.length === 0) {
      return (
        <div className="py-8 text-center text-gray-500">
          Aucune donnée dans cette catégorie
        </div>
      );
    }

    return (
      <div>
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => exportToExcel(data, exportFilename, columns.map((c) => ({ key: c.key, label: c.label })))}
            className="flex items-center gap-2 rounded border border-rgpd-green px-4 py-2 text-sm font-medium text-rgpd-green transition-colors hover:bg-rgpd-green hover:text-white"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Exporter en Excel
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {column.render ? column.render(row[column.key as keyof TableRow], row) : (row[column.key as keyof TableRow] as React.ReactNode)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCoprosOk = () =>
    renderTable(
      coprosOk,
      [
        { key: 'immatriculation', label: 'Immatriculation' },
        { key: 'name', label: 'Nom (fichier)' },
        { key: 'rnicName', label: 'Nom (RNIC)' },
        { key: 'rnicAddress', label: 'Adresse RNIC' },
        { key: 'rnicCity', label: 'Ville' },
        { key: 'rnicPostalCode', label: 'Code postal' },
        {
          key: 'addressMatched',
          label: 'Adresse OK',
          render: (value: boolean) =>
            value ? (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
            ),
        },
      ],
      'copros-ok-rnic',
    );

  const renderCoprosWeHave = () =>
    renderTable(
      coprosWeHave,
      [
        { key: 'immatriculation', label: 'Immatriculation' },
        { key: 'name', label: 'Nom (fichier)' },
        { key: 'existingCoproName', label: 'Nom (base)' },
        { key: 'existingCoproId', label: 'ID Copro' },
      ],
      'copros-deja-en-base',
    );

  const renderCoprosNotInRnic = () =>
    renderTable(
      coprosNotInRnic,
      [
        { key: 'immatriculation', label: 'Immatriculation' },
        { key: 'name', label: 'Nom' },
        { key: 'reason', label: 'Raison' },
      ],
      'copros-non-trouvees-rnic',
    );

  const renderAddressMatchFailed = () =>
    renderTable(
      addressMatchFailed,
      [
        { key: 'immatriculation', label: 'Immatriculation' },
        { key: 'name', label: 'Nom' },
        { key: 'reason', label: 'Raison' },
      ],
      'copros-adresse-non-matchee',
    );

  const renderRnicNotInFile = () =>
    renderTable(
      rnicNotInFile,
      [
        { key: 'immatriculation', label: 'Immatriculation' },
        { key: 'name', label: 'Nom (RNIC)' },
        { key: 'rnicAddress', label: 'Adresse RNIC' },
      ],
      'copros-rnic-absentes-fichier',
    );

  const renderTabContent = () => {
    switch (activeTab) {
    case 'ok':
      return renderCoprosOk();
    case 'weHave':
      return renderCoprosWeHave();
    case 'notInRnic':
      return renderCoprosNotInRnic();
    case 'addressFailed':
      return renderAddressMatchFailed();
    case 'rnicNotInFile':
      return renderRnicNotInFile();
    default:
      return null;
    }
  };

  return (
    <div className="mt-12">
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Résultat de la comparaison</h2>
          <span className="text-sm text-gray-500">
            {summary.totalInFile} copropriétés dans le fichier
          </span>
        </div>

        <p className="mb-6 text-gray-600">
          Cette comparaison n'a effectué aucune modification en base de données. Elle vous permet de visualiser les écarts avant d'importer en base réelle.
        </p>
        <div className="mb-6 flex w-full items-center justify-center">
          <button
            onClick={() => navigate(ROUTES.ADMIN_IMPORT_HISTORY)}
            className="rounded bg-rgpd-green px-2 py-1 text-base text-white shadow-md transition-colors hover:bg-rgpd-green/80"
          >
            Aller sur la liste des imports
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-5">
          <StatCard
            icon={<CheckCircleIcon className="h-6 w-6" />}
            title="OK (RNIC)"
            value={summary.totalOk}
            isActive={activeTab === 'ok'}
            onClick={() => setActiveTab('ok')}
            colorClass="text-green-500"
          />
          <StatCard
            icon={<InformationCircleIcon className="h-6 w-6" />}
            title="Déjà en base"
            value={summary.totalWeHave}
            isActive={activeTab === 'weHave'}
            onClick={() => setActiveTab('weHave')}
            colorClass="text-blue-500"
          />
          <StatCard
            icon={<XCircleIcon className="h-6 w-6" />}
            title="Pas dans RNIC (selon immat)"
            value={summary.totalNotInRnic}
            isActive={activeTab === 'notInRnic'}
            onClick={() => setActiveTab('notInRnic')}
            colorClass="text-red-500"
          />
          <StatCard
            icon={<MapPinIcon className="h-6 w-6" />}
            title="Adresse non matchée"
            value={summary.totalAddressFailed}
            isActive={activeTab === 'addressFailed'}
            onClick={() => setActiveTab('addressFailed')}
            colorClass="text-orange-500"
          />
          <StatCard
            icon={<ExclamationTriangleIcon className="h-6 w-6" />}
            title="RNIC pas dans fichier (selon immat)"
            value={summary.totalRnicNotInFile}
            isActive={activeTab === 'rnicNotInFile'}
            onClick={() => setActiveTab('rnicNotInFile')}
            colorClass="text-yellow-500"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ComparisonResultDisplay;


